/**
 * Athena Voice Engine V2
 *
 * A sessão do SpeechRecognition é transitória. O turno humano é persistido
 * em TurnBuffer até uma decisão explícita (manual) ou endpointing conservador
 * baseado no áudio real (contínuo). O Web Speech continua sendo somente STT de
 * transição: ele não decide mais quando uma pessoa terminou de pensar.
 */
import { WebSpeechAdapter } from './adapters/WebSpeechAdapter';
import { recordVoiceDiagnostic } from './voiceDiagnostics';

const S = {
  IDLE: 'IDLE', PASSIVE: 'PASSIVE_LISTENING', STARTING: 'STARTING',
  LISTENING: 'LISTENING', PAUSED: 'PAUSED', RESTARTING: 'RESTARTING',
  FINALIZING: 'FINALIZING', PROCESSING: 'PROCESSING', SPEAKING: 'SPEAKING',
  SILENT: 'SILENT', ERROR: 'ERROR',
};

const HOME = { IDLE: 'passive', PASSIVE: 'passive', STARTING: 'active', LISTENING: 'active', PAUSED: 'active', RESTARTING: 'active', FINALIZING: 'active', PROCESSING: 'processing', SPEAKING: 'speaking', SILENT: 'silent', ERROR: 'passive' };
const WORKSPACE = { IDLE: 'idle', PASSIVE: 'idle', STARTING: 'listening', LISTENING: 'listening', PAUSED: 'listening', RESTARTING: 'listening', FINALIZING: 'listening', PROCESSING: 'processing', SPEAKING: 'speaking', SILENT: 'idle', ERROR: 'idle' };

// O Web Speech encerra sessões por decisão do próprio navegador. Uma pausa um
// pouco maior evita ciclos de start/end agressivos, especialmente no Chrome
// para Android, sem encerrar o turno do usuário.
const RESTART_DELAY_MS = 700;
// O SpeechRecognizer do Android encerra uma sessão ao detectar uma pausa.
// No modo manual isso não pode alterar a interface nem concluir o turno: a
// pessoa continua "gravando" até tocar novamente. Quando o serviço nativo não
// consegue se manter aberto, rearmamos apenas o transporte em segundo plano,
// sem passar pelo estado RESTARTING e sem criar outro TurnBuffer.
const NATIVE_MANUAL_REARM_DELAY_MS = 320;
// Em Chrome Android o resultado final pode chegar bem depois de onend. Uma
// janela curta fazia o segundo toque apagar o turno antes da transcrição.
const FINAL_SETTLEMENT_MS = 1400;
const FINAL_FALLBACK_MS = 5000;
// No Android, `recognition.stop()` chamado no mesmo instante do toque pode
// cancelar o resultado final da última frase. Mantemos a sessão viva por uma
// janela curta e só então pedimos o encerramento limpo.
const MANUAL_RESULT_GRACE_MS = 700;
const CONTINUOUS_MIN_PAUSE_MS = 5200;
const CONTINUOUS_INCOMPLETE_EXTENSION_MS = 6500;
const CONTINUOUS_MAX_WAIT_MS = 14000;

const incompleteEnding = (text) => {
  const value = text.trim().toLocaleLowerCase('pt-BR');
  if (!value) return true;
  if (/[,;:…]|\b(e|ou|mas|porque|que|pra|para|com|de|da|do|na|no|em|a|o|um|uma|talvez|acho|então|entao|espera)$/u.test(value)) return true;
  return /\b(vou|queria|poderia|preciso|acho|talvez|quando|se|porque|como|sobre|primeiro)$/u.test(value);
};

const normalize = (value) => value.replace(/\s+/g, ' ').trim();

const DIAGNOSTIC_EVENT = {
  turn_start: 'recordingStart',
  recognition_start: 'sttStart',
  microphone_ready: 'recordingStart',
  vad_speech_start: 'speechStart',
  speech_start: 'speechStart',
  vad_speech_end: 'speechEnd',
  speech_end: 'speechEnd',
  stt_interim: 'sttResult',
  stt_final: 'sttResult',
  turn_finalize: 'turnEnd',
  barge_in: 'bargeIn',
  speech_interrupted: 'cancel',
  recognition_error: 'error',
  microphone_error: 'error',
  recognition_unavailable: 'error',
  recognition_start_failed: 'error',
  capture_empty: 'error',
};

export class VoiceEngineV2 {
  continuousMode = false;
  isMobile = false;
  onStateChange = null;
  onDispatch = null;
  onWakeWord = null;
  onPartialResult = null;
  onCaptureFailure = null;
  onPermissionStateChange = null;
  // Nível de presença visual (0–1) emitido pelo reconhecedor nativo. Não
  // carrega áudio nem é usado para decidir quando enviar uma mensagem.
  onVoiceLevelChange = null;
  // Feedback do segundo toque quando o SpeechRecognizer acabou entre duas
  // sessões manuais e não há um adapter ativo para receber `stop()`.
  onManualFinishWithoutAdapter = null;
  onDebug = null;
  // Providers can replace only the STT transport while retaining the shared
  // turn buffer, manual/continuous behavior and endpointing below.
  speechAdapterFactory = null;
  speechRecognitionSupported = null;
  voiceProvider = 'web_speech';

  _state = S.IDLE;
  _map = HOME;
  _destroyed = false;
  _focusMode = false;
  _manualMode = false;
  _adapter = null;
  _adapterGeneration = 0;
  _restartTimer = null;
  _settlementTimer = null;
  _fallbackTimer = null;
  _manualStopTimer = null;
  _turnTimer = null;
  _turnCounter = 0;
  _recognitionCounter = 0;
  _turn = null;
  _stream = null;
  _audioContext = null;
  _analyser = null;
  _audioFrame = null;
  _audioTimer = null;
  _speechActive = false;
  _lastVoiceAt = 0;
  _lastMicActivityAt = 0;
  _lastError = null;
  _lastRecognitionEvent = null;
  _voiceLevel = 0;

  constructor(options = {}) {
    Object.assign(this, options);
    // A chamada visual usa os mesmos estados legíveis da Home (`active`,
    // `speaking`, etc.), mas continua identificada como uma superfície própria
    // para telemetria e para futuros ajustes de interface.
    this._map = options.context === 'workspace' ? WORKSPACE : HOME;
  }

  get state() {
    if (this._focusMode && [S.IDLE, S.PASSIVE].includes(this._state)) return 'focus';
    return this._map[this._state] || 'passive';
  }

  get diagnostics() {
    return {
      engine: 'v2', state: this._state, microphone: !!this._stream,
      vad: !!this._audioTimer, speech: this._speechActive,
      turn_id: this._turn?.id || null, recognition_session: this._turn?.recognitionSessionId || null,
      transcript_chars: this._turn ? this._turnText().length : 0, last_error: this._lastError,
      last_recognition_event: this._lastRecognitionEvent,
      speech_recognition_supported: typeof window !== 'undefined'
        && this._speechSupported(),
      platform: typeof navigator === 'undefined' ? 'unknown' : navigator.platform || 'unknown',
      browser: typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent,
      pwa: typeof window !== 'undefined' && !!(window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone),
      stt_provider: this.voiceProvider,
      voice_provider: 'remote_edge',
      native_fallback_attempted: false,
    };
  }

  _debug(event, data = {}) {
    const entry = { timestamp: new Date().toISOString(), event, state: this._state, turn_id: this._turn?.id || null, recognition_session: this._turn?.recognitionSessionId || null, ...data };
    recordVoiceDiagnostic(DIAGNOSTIC_EVENT[event] || 'voiceState', {
      platform: this.voiceProvider === 'android_speech_recognizer' ? 'android' : 'web',
      provider: this.voiceProvider,
      state: this._state,
      turnId: this._turn?.id || undefined,
      sessionId: this._turn?.recognitionSessionId || undefined,
      errorCode: typeof data?.error === 'string'
        ? data.error
        : (typeof data?.reason === 'string' ? data.reason : undefined),
    });
    // Diagnóstico opt-in: não grava áudio nem envia transcrições para servidor.
    // Pode ser inspecionado em DevTools sem alterar a experiência do usuário.
    if (typeof window !== 'undefined') {
      const previous = window.__athenaVoiceDebugLog__ || [];
      window.__athenaVoiceDebugLog__ = [...previous.slice(-199), entry];
      window.__athenaVoiceDiagnostics = this.diagnostics;
    }
    this.onDebug?.(entry);
    if (window.__ATHENA_VOICE_DEBUG__) console.debug('[AthenaVoiceV2]', entry);
  }

  _emit() { this.onStateChange?.(this.state); }
  _move(next) { if (!this._destroyed && this._state !== next) { this._state = next; this._emit(); this._debug('state', { next }); } }
  _setVoiceLevel(value) {
    const numeric = Number(value);
    const level = Number.isFinite(numeric) ? Math.max(0, Math.min(1, numeric)) : 0;
    if (Math.abs(level - this._voiceLevel) < 0.003) return;
    this._voiceLevel = level;
    this.onVoiceLevelChange?.(level);
  }
  _speechSupported() {
    if (typeof this.speechRecognitionSupported === 'function') {
      try { return Boolean(this.speechRecognitionSupported()); } catch { return false; }
    }
    if (typeof this.speechRecognitionSupported === 'boolean') return this.speechRecognitionSupported;
    if (this.speechAdapterFactory) return true;
    return typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  async _ensureMic() {
    if (this._stream || !navigator.mediaDevices?.getUserMedia) return !!this._stream;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false,
      });
      if (this._destroyed) { stream.getTracks().forEach((track) => track.stop()); return false; }
      this._stream = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return true;
      this._audioContext = new AudioCtx();
      await this._audioContext.resume?.();
      const source = this._audioContext.createMediaStreamSource(stream);
      this._analyser = this._audioContext.createAnalyser();
      this._analyser.fftSize = 1024;
      this._audioFrame = new Uint8Array(this._analyser.fftSize);
      source.connect(this._analyser);
      this._audioTimer = window.setInterval(() => this._sampleAudio(), 80);
      this._debug('microphone_ready');
      return true;
    } catch (error) {
      this._lastError = error?.name || 'microphone_unavailable';
      this._debug('microphone_error', { error: this._lastError });
      return false;
    }
  }

  _sampleAudio() {
    if (!this._analyser || !this._audioFrame || this._destroyed) return;
    this._analyser.getByteTimeDomainData(this._audioFrame);
    let sum = 0;
    for (const sample of this._audioFrame) { const value = (sample - 128) / 128; sum += value * value; }
    const rms = Math.sqrt(sum / this._audioFrame.length);
    // Threshold intentionally low because browser constraints already apply noise suppression.
    const voiced = rms > 0.018;
    const timestamp = Date.now();
    if (voiced) {
      this._lastMicActivityAt = timestamp;
      if (!this._speechActive) {
        this._speechActive = true;
        if (this._state === S.PAUSED) this._move(S.LISTENING);
        this._debug('vad_speech_start', { rms });
      }
    } else if (this._speechActive && timestamp - this._lastMicActivityAt > 420) {
      this._speechActive = false;
      this._lastVoiceAt = this._lastMicActivityAt;
      this._debug('vad_speech_end', { rms });
    }
  }

  _startTurnTimer() {
    if (this._turnTimer) clearInterval(this._turnTimer);
    this._turnTimer = window.setInterval(() => this._evaluateContinuousTurn(), 250);
  }

  _stopTurnTimer() { if (this._turnTimer) { clearInterval(this._turnTimer); this._turnTimer = null; } }

  _releaseMic() {
    if (this._audioTimer) clearInterval(this._audioTimer);
    this._audioTimer = null;
    this._audioContext?.close?.(); this._audioContext = null;
    this._analyser = null; this._audioFrame = null;
    this._stream?.getTracks?.().forEach((track) => track.stop()); this._stream = null;
    this._speechActive = false;
  }

  _evaluateContinuousTurn() {
    if (this._manualMode || ![S.LISTENING, S.PAUSED].includes(this._state) || !this._turn || !this.continuousMode) return;
    const text = this._turnText();
    if (text.length < 3 || this._speechActive || !this._lastVoiceAt) return;
    const silence = Date.now() - this._lastVoiceAt;
    const threshold = incompleteEnding(text) ? CONTINUOUS_MIN_PAUSE_MS + CONTINUOUS_INCOMPLETE_EXTENSION_MS : CONTINUOUS_MIN_PAUSE_MS;
    // VAD is a signal, not authority alone: an automatic conclusion needs both
    // a complete transcript and a conservative silent window. Incomplete speech
    // gets a much longer thinking allowance.
    if (silence >= threshold || (silence >= CONTINUOUS_MAX_WAIT_MS && !incompleteEnding(text))) {
      this._debug('turn_endpoint', { silence, threshold, semantic_complete: !incompleteEnding(text) });
      this._finalizeTurn('continuous_endpoint');
    } else if (this._state === S.LISTENING && silence >= 500) {
      this._move(S.PAUSED);
    } else if (this._state === S.PAUSED && this._speechActive) {
      this._move(S.LISTENING);
    }
  }

  _newTurn(mode) {
    this._turn = {
      id: `turn-${Date.now()}-${++this._turnCounter}`, mode, startedAt: Date.now(),
      recognitionSessionId: null,
      // resultIndex reinicia em zero a cada sessão de SpeechRecognition. Por
      // isso, ele nunca pode ser a identidade global de um trecho do turno.
      confirmed: new Map(), interim: new Map(), order: new Map(), segmentCounter: 0,
      manualStartCueClaimed: false,
      explicitlyFinished: false,
    };
    this._lastVoiceAt = 0;
    this._speechActive = false;
    this._setVoiceLevel(0);
    this._debug('turn_start', { mode });
  }

  _turnText() {
    if (!this._turn) return '';
    const parts = new Map(this._turn.confirmed);
    for (const [index, value] of this._turn.interim) if (!parts.has(index)) parts.set(index, value);
    return normalize(
      [...parts.entries()]
        .sort(([a], [b]) => (this._turn.order.get(a) || 0) - (this._turn.order.get(b) || 0))
        .map(([, value]) => value)
        .join(' '),
    );
  }

  _publishPartial() { this.onPartialResult?.(this._turnText()); }

  _clearTimers() {
    this._stopTurnTimer();
    if (this._restartTimer) clearTimeout(this._restartTimer);
    if (this._settlementTimer) clearTimeout(this._settlementTimer);
    if (this._fallbackTimer) clearTimeout(this._fallbackTimer);
    if (this._manualStopTimer) clearTimeout(this._manualStopTimer);
    this._restartTimer = this._settlementTimer = this._fallbackTimer = null;
    this._manualStopTimer = null;
  }

  _stopRecognition({ abort = true } = {}) {
    this._adapterGeneration++;
    const adapter = this._adapter; this._adapter = null;
    this._setVoiceLevel(0);
    if (!adapter) return;
    if (abort) adapter.destroy(); else adapter.stop();
  }

  _isNativeManualTurn(mode) {
    return mode === 'manual'
      && this.voiceProvider === 'android_speech_recognizer'
      && this._manualMode
      && Boolean(this._turn)
      && !this._turn.explicitlyFinished;
  }

  _scheduleNativeManualRearm() {
    if (this._destroyed
      || !this._isNativeManualTurn('manual')
      || this._adapter
      || this._restartTimer
      || [S.FINALIZING, S.PROCESSING, S.SPEAKING, S.SILENT].includes(this._state)) return;

    // Importante: não movemos para S.RESTARTING. A captura manual continua
    // visualmente ativa e o único gesto que a encerra é o segundo toque.
    this._debug('manual_native_rearm_queued', { delay: NATIVE_MANUAL_REARM_DELAY_MS });
    this._restartTimer = window.setTimeout(() => {
      this._restartTimer = null;
      if (!this._destroyed && this._isNativeManualTurn('manual') && !this._adapter) {
        this._debug('manual_native_rearm_start');
        this._startRecognition('manual');
      }
    }, NATIVE_MANUAL_REARM_DELAY_MS);
  }

  _startRecognition(mode) {
    if (this._destroyed || !this._turn || this._adapter || [S.FINALIZING, S.PROCESSING, S.SPEAKING].includes(this._state)) return;
    if (!this._speechSupported()) {
      this._lastError = 'recognition_unavailable';
      const failure = { reason: this._lastError, speechRecognitionSupported: false, finalization: 'capture_start' };
      this._debug('recognition_unavailable', failure);
      this.onCaptureFailure?.(failure);
      this.setIdle();
      return;
    }
    const generation = ++this._adapterGeneration;
    const sessionId = `recognition-${Date.now()}-${++this._recognitionCounter}`;
    this._turn.recognitionSessionId = sessionId;
    // O reconhecimento contínuo nativo é mais instável em PWAs móveis. No
    // modo manual usamos sessões curtas e as reunimos no mesmo TurnBuffer; no
    // contínuo mantemos a sessão aberta normalmente.
    const Adapter = this.speechAdapterFactory || WebSpeechAdapter;
    const adapter = new Adapter({
      lang: 'pt-BR',
      // O manual precisa ser uma sessão curta de ditado: o navegador entrega
      // o resultado final quando a pessoa para de falar. Mantê-lo como
      // contínuo no Android fazia alguns WebViews encerrarem o segundo toque
      // sem emitir resultado. O modo contínuo é o único que permanece aberto.
      continuous: mode === 'continuous',
      interimResults: true,
      sessionId,
      // Unlike the recognizer session id, the human manual turn remains
      // stable across Android's transparent internal retries. It is used only
      // to deduplicate the start/end feedback tones at the native boundary.
      manualTurnId: this._turn.id,
      // The first *logical* manual capture may play one acknowledgement. A
      // retry after a native speech timeout must never be audible.
      playStartCue: mode === 'manual' && !this._turn.manualStartCueClaimed,
      onPermissionState: (detail) => {
        if (generation !== this._adapterGeneration) return;
        this._debug('microphone_permission', detail || {});
        this.onPermissionStateChange?.(detail || {});
      },
    });
    adapter.onEvent = (event, detail) => {
      if (generation !== this._adapterGeneration) return;
      // RMS é uma indicação visual de presença. Ele não deve alimentar o
      // log/telemetria de cada frame, nem alterar o estado do turno.
      if (event === 'recognition_level') {
        this._setVoiceLevel(detail?.level);
        return;
      }
      this._lastRecognitionEvent = event;
      this._debug(event, { detail });
      if (event === 'speech_start') { this._speechActive = true; this._lastMicActivityAt = Date.now(); if (this._state === S.PAUSED) this._move(S.LISTENING); }
      // Quando VAD não está disponível (permissão/AudioContext recusado), o
      // speech_end é a única indicação de silêncio. Sem limpar esta flag, o
      // modo contínuo ficava preso em “falando” e jamais despachava o turno.
      if (event === 'speech_end') { this._speechActive = false; this._lastVoiceAt = Date.now(); }
    };
    adapter.onResult = (value, isFinal, meta = {}) => {
      if (generation !== this._adapterGeneration || !this._turn) return;
      const resultIndex = Number.isInteger(meta.resultIndex) ? meta.resultIndex : 0;
      // Um rearm nativo transparente pode reutilizar resultIndex=0 para cada
      // frase. O adaptador fornece segmentId nesses casos, preservando todas
      // as frases do mesmo turno manual em vez de sobrescrever a anterior.
      const segmentId = typeof meta.segmentId === 'string' && meta.segmentId
        ? meta.segmentId
        : String(resultIndex);
      const index = `${sessionId}:${segmentId}`;
      if (!this._turn.order.has(index)) this._turn.order.set(index, ++this._turn.segmentCounter);
      const text = normalize(value || ''); if (!text) return;
      if (isFinal) { this._turn.confirmed.set(index, text); this._turn.interim.delete(index); this._debug('stt_final', { index, chars: text.length }); }
      else { this._turn.interim.set(index, text); this._debug('stt_interim', { index, chars: text.length }); }
      // Quando há captura controlada, a marca de atividade pertence ao VAD de
      // áudio. O relógio do STT só é fallback para navegadores que recusam
      // getUserMedia, nunca autoridade de endpointing.
      if (!this._stream) this._lastVoiceAt = Date.now();
      this._publishPartial();
    };
    adapter.onError = (event) => {
      if (generation !== this._adapterGeneration) return;
      const error = event?.error || 'unknown'; this._lastError = error; this._debug('recognition_error', { error });
      if (error === 'not-allowed' || error === 'service-not-allowed') {
        this._adapter = null;
        this.onCaptureFailure?.({
          reason: error,
          speechRecognitionSupported: true,
          finalization: 'recognition_permission',
        });
        // A permission prompt is recoverable after the person grants it. Do
        // not leave the engine in SILENT: that state makes the next tap a
        // no-op while the UI appears ready.
        this.setIdle();
        return;
      }
      if (this._isNativeManualTurn(mode) && event?.recoverable !== false) {
        // O Android costuma emitir no_match/speech_timeout antes de encerrar
        // uma sessão por silêncio. Não transforme isso em envio, em erro
        // visível ou em RESTARTING; espere o `ended` para rearmar o STT de
        // maneira silenciosa. Se o Java já rearmou a mesma sessão, manter o
        // adapter vivo também evita apagar os listeners do novo trecho.
        this._debug('manual_native_recognition_pause', { error });
        return;
      }
      if (event?.recoverable === false) {
        this._adapter = null;
        this.onCaptureFailure?.({
          reason: error,
          speechRecognitionSupported: true,
          finalization: 'recognition_nonrecoverable',
        });
        this.setIdle();
        return;
      }
      this._adapter = null;
      if (!this._turn?.explicitlyFinished) this._scheduleRecognitionRestart(mode);
    };
    adapter.onEnd = () => {
      if (generation !== this._adapterGeneration) return;
      this._adapter = null;
      this._debug('recognition_end');
      // End of browser session never ends a UserTurn.
      if (this._turn?.explicitlyFinished) this._settleExplicitFinish();
      else if (this._isNativeManualTurn(mode)) this._scheduleNativeManualRearm();
      else this._scheduleRecognitionRestart(mode);
    };
    if (adapter.start()) {
      if (mode === 'manual') this._turn.manualStartCueClaimed = true;
      this._adapter = adapter;
      this._debug('recognition_start', { sessionId });
    }
    else {
      this._lastError = 'recognition_start_failed';
      const failure = { reason: this._lastError, speechRecognitionSupported: true, finalization: 'capture_start' };
      this._debug('recognition_start_failed', failure);
      this.onCaptureFailure?.(failure);
      this.setIdle();
    }
  }

  _scheduleRecognitionRestart(mode) {
    if (this._destroyed || !this._turn || this._turn.explicitlyFinished || this._restartTimer || [S.PROCESSING, S.SPEAKING, S.SILENT].includes(this._state)) return;
    this._move(S.RESTARTING);
    this._restartTimer = window.setTimeout(() => {
      this._restartTimer = null;
      if (!this._destroyed && this._turn && !this._turn.explicitlyFinished && !this._adapter) {
        this._move(S.LISTENING); this._startRecognition(mode);
      }
    }, RESTART_DELAY_MS);
  }

  _settleExplicitFinish() {
    if (!this._turn?.explicitlyFinished || this._settlementTimer) return;
    this._settlementTimer = window.setTimeout(() => this._finalizeTurn('manual_explicit'), FINAL_SETTLEMENT_MS);
  }

  _finalizeTurn(reason) {
    if (!this._turn || [S.PROCESSING, S.SPEAKING].includes(this._state)) return;
    this._move(S.FINALIZING);
    this._clearTimers();
    const text = this._turnText();
    this._debug('turn_finalize', { reason, chars: text.length });
    this._stopRecognition();
    this._turn = null;
    // Uma palavra também pode ser uma intenção válida ("calendário",
    // "ajuda", "pare"). Só voltamos ao repouso quando realmente não houve
    // transcrição; antes, frases curtas eram descartadas silenciosamente.
    if (!text) {
      // Nunca esconda uma captura vazia. Este era exatamente o percurso que
      // fazia o segundo toque parecer ignorado no celular: o navegador não
      // emitia onresult e a interface retornava ao repouso em silêncio.
      const failure = {
        reason: this._lastError || 'no_transcript',
        finalization: reason,
        recognitionEvent: this._lastRecognitionEvent,
        // NativeVoiceProvider não possui `window.SpeechRecognition`; consulte
        // sempre a capacidade do provider selecionado.
        speechRecognitionSupported: this._speechSupported(),
      };
      this._debug('capture_empty', failure);
      this.onCaptureFailure?.(failure);
      this.setIdle();
      return;
    }
    this._move(S.PROCESSING);
    this.onDispatch?.(text);
  }

  _startCapture(mode) {
    if (this._destroyed || !this._turn) return;
    this._move(S.LISTENING);

    // Em Android/iOS instalados, SpeechRecognition.start() pode exigir uma
    // ativação de usuário no mesmo ciclo do toque. Antes, este start vinha
    // depois de `await getUserMedia()`, perdendo a ativação e falhando apenas
    // no celular. O reconhecimento agora começa de forma síncrona; o VAD é um
    // aprimoramento paralelo, não uma dependência para captar a fala.
    this._startRecognition(mode);
    if (mode === 'continuous') this._startTurnTimer();

    // SpeechRecognition já abre o microfone por conta própria. Abrir também
    // getUserMedia para o VAD criava dois consumidores concorrentes do mesmo
    // hardware. Em Chrome Android/PWA isso frequentemente deixava o serviço
    // de reconhecimento ativo, porém sem entregar onresult — a animação
    // parecia ouvir e o segundo toque terminava em silêncio.
    //
    // No manual o VAD não é necessário porque o segundo toque define o fim.
    // No contínuo móvel usamos os eventos speech_start/speech_end do próprio
    // SpeechRecognition. O VAD fica restrito ao desktop, onde é estável e só
    // melhora a detecção de pausa.
    if (mode !== 'continuous' || this.isMobile) return;

    void this._ensureMic().then(() => {
      // Se o usuário encerrou a fala enquanto a permissão era exibida, não
      // mantenha um stream de microfone aberto em segundo plano.
      if (this._destroyed || !this._turn || [S.PROCESSING, S.SPEAKING, S.IDLE, S.SILENT].includes(this._state)) {
        this._releaseMic();
      }
    });
  }

  startManual() {
    if (this._destroyed || [S.PROCESSING, S.SPEAKING, S.SILENT, S.FINALIZING].includes(this._state)) return;
    if (this._manualMode && this._turn) return;
    this._clearTimers(); this._stopRecognition(); this._manualMode = true; this._newTurn('manual'); this._move(S.STARTING);
    this._startCapture('manual');
  }

  // A decisão de iniciar/parar precisa ficar no motor, não no estado visual
  // do React. Em celulares o callback do segundo toque pode acontecer antes
  // de o React sincronizar "active" na interface, o que fazia `startManual`
  // ser chamado novamente e ignorar o toque sem finalizar a fala.
  toggleManual() {
    if (this._destroyed || [S.PROCESSING, S.SPEAKING, S.SILENT].includes(this._state)) return false;
    if (this._manualMode && this._turn) {
      if (!this._turn.explicitlyFinished) this.finishManual();
      return true;
    }
    this.startManual();
    return true;
  }

  finishManual() {
    if (this._destroyed || !this._manualMode || !this._turn || this._turn.explicitlyFinished) return;
    const nativeManual = this.voiceProvider === 'android_speech_recognizer';
    this._turn.explicitlyFinished = true; this._move(S.FINALIZING); this._stopTurnTimer();
    // Cancela um rearm nativo pendente antes de pedir o encerramento. Sem
    // isso, um segundo toque durante a curta janela entre frases podia abrir
    // outra sessão do SpeechRecognizer depois de o usuário já ter enviado.
    if (this._restartTimer) { clearTimeout(this._restartTimer); this._restartTimer = null; }
    this._debug('manual_finish_requested');
    const stopAndSettle = () => {
      this._manualStopTimer = null;
      if (this._destroyed || !this._turn?.explicitlyFinished || this._state !== S.FINALIZING) return;
      const adapter = this._adapter;
      if (adapter) adapter.stop();
      else if (nativeManual) this.onManualFinishWithoutAdapter?.(this._turn.id);
      // Para Web, a janela abaixo protege o último onresult mesmo quando o
      // navegador não devolve onend. No nativo, espere o onResults/ended do
      // plugin (ou seu fallback de stop) antes de iniciar a liquidação: o
      // segundo toque agora é imediato e uma janela fixa de 1,4 s poderia
      // descartar a frase final de um serviço Android mais lento.
      if (!nativeManual || !adapter) this._settleExplicitFinish();
    };
    if (nativeManual) {
      // No app Android o segundo toque é o botão de envio: peça ao plugin
      // nativo para encerrar imediatamente. Ele mantém o resultado final até
      // onResults/onError, enquanto o engine só despacha após a liquidação.
      stopAndSettle();
    } else {
      // Chrome Android/PWA pode perder o último `onresult` se stop ocorrer no
      // mesmo frame. Preserve a pequena janela de segurança somente no Web.
      this._manualStopTimer = window.setTimeout(stopAndSettle, MANUAL_RESULT_GRACE_MS);
    }
    // Safety only. `onend` + settlement is the normal route, never this timer.
    this._fallbackTimer = window.setTimeout(() => this._finalizeTurn('manual_safety_fallback'), FINAL_FALLBACK_MS);
  }

  // Ação explícita de “parar e enviar” durante a conversa contínua. O modo
  // permanece ligado: quando Athena concluir a resposta, doneSpeaking()
  // reabre automaticamente um novo turno de escuta.
  finishActive() {
    if (this._destroyed || this._manualMode || !this._turn || [S.PROCESSING, S.SPEAKING, S.FINALIZING].includes(this._state)) return;
    this._debug('continuous_finish_requested');
    this._finalizeTurn('continuous_explicit_finish');
  }

  startActive() {
    if (this._destroyed || [S.PROCESSING, S.SPEAKING, S.SILENT, S.FINALIZING].includes(this._state)) return;
    if (!this._manualMode && this._turn && [S.STARTING, S.LISTENING, S.PAUSED, S.RESTARTING].includes(this._state)) return;
    this._clearTimers(); this._stopRecognition(); this._manualMode = false; this._newTurn('continuous'); this._move(S.STARTING);
    this._startCapture('continuous');
  }

  startPassive() {
    // Passive wake word remains compatibility-only. It does not create a user turn.
    if (this.isMobile || this._destroyed || [S.SPEAKING, S.PROCESSING, S.SILENT].includes(this._state)) return;
    this.stop(); this._manualMode = false; this._move(S.PASSIVE);
    const generation = ++this._adapterGeneration;
    const adapter = new WebSpeechAdapter({ lang: 'pt-BR', continuous: true, interimResults: false });
    adapter.onResult = (value, isFinal) => {
      if (generation !== this._adapterGeneration || !isFinal) return;
      const match = normalize(value).toLocaleLowerCase('pt-BR').match(/(?:^|\s)(?:ei\s+|hey\s+|oi\s+|olha\s+)?(athena|atena)(?:\s|$|[,.!?])(.*)$/u);
      if (!match) return;
      this._stopRecognition(); const command = normalize(match[2] || '');
      this.onWakeWord?.(command);
    };
    adapter.onEnd = () => { if (generation === this._adapterGeneration && this._state === S.PASSIVE) this._schedulePassiveRestart(); };
    adapter.onError = () => { if (generation === this._adapterGeneration && this._state === S.PASSIVE) this._schedulePassiveRestart(); };
    if (adapter.start()) this._adapter = adapter;
  }

  _schedulePassiveRestart() {
    if (this._restartTimer) return;
    this._restartTimer = window.setTimeout(() => { this._restartTimer = null; if (this._state === S.PASSIVE) this.startPassive(); }, 1000);
  }

  startSpeaking() {
    if (this._destroyed) return;
    this._clearTimers(); this._stopRecognition(); this._turn = null; this._manualMode = false;
    // Não permita que a caixa de som seja interpretada como nova fala do
    // usuário. O microfone é reaberto apenas depois do fim do áudio.
    this._releaseMic();
    this._move(S.SPEAKING);
  }

  doneSpeaking() {
    if (this._destroyed || this._state !== S.SPEAKING) return;
    // `startActive` bloqueia propositalmente SPEAKING. Antes esta função
    // tentava reiniciar antes de sair desse estado e o modo contínuo morria
    // após a primeira resposta. A transição para IDLE é intencional e ocorre
    // apenas depois de a superfície confirmar que o áudio terminou.
    this._move(S.IDLE);
    this._debug('speech_finished', { restart: this.continuousMode ? 'continuous' : this.isMobile ? 'idle' : 'passive' });
    if (this.continuousMode) this.startActive();
    else if (!this.isMobile) this.startPassive();
  }

  // A resposta pode permanecer somente em texto quando o TTS remoto estiver
  // indisponível. Ainda assim, o turno precisa encerrar pelo mesmo caminho de
  // retomada para não deixar manual/contínuo presos em PROCESSING.
  finishResponseWithoutSpeech() {
    if (this._destroyed) return;
    if (this._state === S.SPEAKING) { this.doneSpeaking(); return; }
    this._move(S.IDLE);
    this._debug('response_finished_without_speech', { restart: this.continuousMode ? 'continuous' : this.isMobile ? 'idle' : 'passive' });
    if (this.continuousMode) this.startActive();
    else if (!this.isMobile) this.startPassive();
  }

  cancelSpeaking(reason = 'speech_interrupted') {
    if (this._destroyed || this._state !== S.SPEAKING) return false;
    this._move(S.IDLE);
    this._debug(reason);
    return true;
  }

  beginBargeIn() {
    // UI invokes this after stopping TTS. Capture starts immediately and the
    // mic stream remains warm, so the first user words are less likely lost.
    if (!this.cancelSpeaking('barge_in')) return false;
    this.startActive();
    return true;
  }

  setProcessing() { if (!this._destroyed) { this._clearTimers(); this._stopRecognition(); this._move(S.PROCESSING); } }
  setIdle() { if (!this._destroyed) { this._clearTimers(); this._stopRecognition(); this._turn = null; this._manualMode = false; this._releaseMic(); this._move(S.IDLE); } }
  stop() { this.setIdle(); }
  mute() { this.setIdle(); this._move(S.SILENT); }
  setContinuousMode(enabled) { this.continuousMode = !!enabled; if (!enabled && !this._manualMode && this._turn) this.setIdle(); }
  setFocusMode(enabled) { this._focusMode = !!enabled; this._emit(); }
  destroy() {
    this._destroyed = true; this._clearTimers(); this._stopRecognition();
    this._releaseMic();
  }
}
